import { ChakraProvider, extendTheme } from '@chakra-ui/react'
import { darken, mix } from 'color2k'
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import { LanguageCode } from '../i18n/types'
import { CLIENT_CONFIG } from './buildConfig'
import { touchSafeButtonVariants, touchSafeHover, touchSafeMenuItem } from './touchSafeTheme'

export interface KnowledgeSite {
  key: string
  name: string
  // Per-source agent tool identifier (`retrieve_knowledge_<key>`) the
  // assistant editor stores in an agent's `tools` list. Server-provided so
  // the frontend never re-derives the backend's name sanitization.
  tool_name: string
}

type QuietChatColors = Partial<
  Record<
    | 'canvas'
    | 'sidebar'
    | 'composer'
    | 'bubble'
    | 'border'
    | 'ink'
    | 'muted'
    | 'paper'
    | 'userBubble'
    | 'userInk'
    | 'selected',
    string
  >
>

export interface ClientConfig {
  client_id: string
  // Static authentication switches. Runtime feature flags are deliberately not
  // here: FeaturesProvider fetches them from the backend.
  auth?: {
    // Username/password login next to Microsoft sign-in. Never a runtime flag.
    custom_login?: boolean
  }
  // Toggleable knowledge sources are fetched per-user via
  // useVisibleKnowledgeSites, not embedded here — a static build-config list
  // would leak restricted source names to ungated users.
  // Languages the switcher offers this client; absent falls back to the default set.
  languages?: LanguageCode[]
  theme: {
    name: string
    // Per-client chat identity and colour overrides; absent keeps the neutral defaults.
    chat?: {
      sidebarSignature?: { markUrl: string; taglineUrl: string; taglineDarkModeUrl?: string; label: string }
      showWordmark?: boolean
      welcomeMark?: 'speech' | 'logo'
      // Rendered height in px of the welcome logo on the empty chat screen.
      welcomeLogoHeight?: number
      logoUrl?: string
      logoDarkModeUrl?: string
      // Each key overrides one `chat.*` token. Absent keys fall back to
      // QUIET_CHAT_DEFAULTS, or for the brand-tinted ones to a mix of `primary`.
      colors?: QuietChatColors & { primary?: string; iconSurface?: string }
      darkColors?: QuietChatColors
    }
    colors: {
      primary: string
      secondary: string
      accent: string
      background: string
      surface: string
      text: {
        primary: string
        secondary: string
        muted: string
      }
      success: string
      warning: string
      error: string
      // Login card overlay color. Defaults to the primary color at 75% opacity;
      // set this to pin an exact value independent of the primary derivation.
      loginCardOverlay?: string
      // Pale brand color backing hover and selected surfaces, replacing the
      // tint otherwise derived from the primary.
      tint?: string
    }
    branding: {
      logoUrl: string
      showLogoInHeader?: boolean
      // A wide wordmark needs a smaller value than the default, which is sized
      // for a roughly square mark.
      logoHeight?: string
      // Absent, logoUrl serves both color modes.
      logoDarkModeUrl?: string
      faviconUrl: string
      agentIconUrl?: string
      internalIconUrl?: string
      companyName: string
      // Display name of the assistant itself. Falls back to companyName-derived
      // naming when absent. Same identity as the vanilla agent name in bootstrap.py.
      assistantName?: string
      tagline: string
      supportEmail: string
      backgroundImage?: string
      // Full company logo shown centered at the top of the login page; absent means no logo.
      loginLogoUrl?: string
    }
    typography: {
      fontFamily: string
      headingFontFamily: string
      weights?: { body: number; heading: number }
      googleFontsUrl?: string
      fontFaces?: { family: string; weight: number; src: string }[]
    }
    icons?: {
      navigation?: Record<string, string>
      agents?: Record<string, string>
      agentChoices?: string[]
    }
    layout: {
      sidebarWidth: string
      headerHeight: string
      borderRadius: string
      loginCardOffsetY?: { base: string; md: string }
    }
    customTexts: {
      welcomeMessage: string
      chatPlaceholder: string
      footerText: string
      thinkingMessages?: Record<string, string[]>
      // Language-keyed copy shown when a user resolves to zero access scopes.
      noAccessMessage?: Record<string, string>
    }
    // Optional support block — when present, the information modal renders a
    // support section with these contact options.
    support?: {
      intro?: string
      items?: Array<{ label: string; value: string; href?: string }>
    }
    // Optional data visibility notice — when present, the information modal
    // renders this string under a translated title.
    dataVisibilityNotice?: string
    // Optional full override of the information modal content, keyed by language
    // code. When present, the modal renders entirely from this block for the
    // active language (falling back to English) and ignores the generic,
    // feature-flag-driven copy from the shared translations.
    informationModal?: Record<
      string,
      {
        title: string
        intro: string
        sections: Array<{ heading: string; items: string[] }>
        notes?: string[]
        // Rendered below the sections, e.g. a schematic of the data the
        // assistant can reach, so users need not guess what is in it.
        image?: { url: string; alt: string; caption?: string }
      }
    >
  }
}

interface ClientContextType {
  config: ClientConfig | null
  isLoading: boolean
  theme: any // Chakra UI theme object
}

const ClientContext = createContext<ClientContextType | undefined>(undefined)

export const useClient = () => {
  const context = useContext(ClientContext)
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider')
  }
  return context
}

interface ClientProviderProps {
  children: ReactNode
  clientId: string
}

// Build a Chakra shade palette from one base color. A positive amount is the
// fraction of the way to white: mixing rather than raising HSL lightness, which
// on a dark base only walks up the same hue at full saturation and never reaches
// a usable tint. A negative amount darkens by that much lightness, and 0 is the
// base color itself. Shades listed in `overrides` are pinned rather than derived.
const makePalette = (color: string, amounts: Record<number, number>, overrides: Record<number, string> = {}) => ({
  ...Object.fromEntries(
    Object.entries(amounts).map(([shade, amount]) => [
      shade,
      amount === 0 ? color : amount > 0 ? mix(color, '#ffffff', amount) : darken(color, -amount),
    ]),
  ),
  ...overrides,
})

// Neutral chat surfaces and text, the same for every client.
// A client overrides any of them through `theme.chat.colors` / `darkColors`.
const QUIET_CHAT_DEFAULTS = {
  light: {
    canvas: 'white',
    sidebar: '#F7F7F7',
    composer: 'white',
    bubble: '#F1F1F1',
    border: '#E5E5E5',
    ink: '#202020',
    muted: '#686868',
    paper: '#FFFFFF',
  },
  dark: {
    canvas: '#212121',
    sidebar: '#181818',
    composer: '#303030',
    bubble: '#303030',
    border: '#424242',
    ink: '#F5F5F5',
    muted: '#ADADAD',
    paper: '#212121',
  },
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children, clientId }) => {
  const [config, setConfig] = useState<ClientConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [theme, setTheme] = useState<any>(null)

  useEffect(() => {
    const loadBuildTimeConfig = async () => {
      try {
        setIsLoading(true)

        // Check for test config override
        const testConfig = (window as any).__TEST_CONFIG__
        const clientConfig = testConfig || (CLIENT_CONFIG as ClientConfig)

        if (testConfig) {
          console.log('Using test config override:', testConfig)
        }

        setConfig(clientConfig)

        if (clientConfig.theme.typography.googleFontsUrl) {
          const preconnect1 = document.createElement('link')
          preconnect1.rel = 'preconnect'
          preconnect1.href = 'https://fonts.googleapis.com'
          document.head.appendChild(preconnect1)

          const preconnect2 = document.createElement('link')
          preconnect2.rel = 'preconnect'
          preconnect2.href = 'https://fonts.gstatic.com'
          preconnect2.crossOrigin = 'anonymous'
          document.head.appendChild(preconnect2)

          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = clientConfig.theme.typography.googleFontsUrl
          document.head.appendChild(link)
        }

        // A brand that names its own pale color uses it for the two shades that
        // back hover and selected surfaces, in place of the derived tint.
        const tint = clientConfig.theme.colors.tint
        const tintShades: Record<number, string> = tint ? { 50: tint, 100: darken(tint, 0.07) } : {}

        const chatPrimary = clientConfig.theme.chat?.colors?.primary ?? clientConfig.theme.colors.primary
        const chatIconSurface = clientConfig.theme.chat?.colors?.iconSurface ?? mix(chatPrimary, '#ffffff', 0.9)
        const chatLight = { ...QUIET_CHAT_DEFAULTS.light, ...clientConfig.theme.chat?.colors }
        const chatDark = { ...QUIET_CHAT_DEFAULTS.dark, ...clientConfig.theme.chat?.darkColors }
        // Dark-mode tints start from this pastel rather than from the primary, so
        // a dark primary still lifts the bubble and selection off the background.
        const chatBrandDark = mix(chatPrimary, '#ffffff', 0.7)

        // Create Chakra UI theme from client config
        const chakraTheme = extendTheme({
          styles: {
            global: {
              '@font-face': clientConfig.theme.typography.fontFaces?.map(
                (font: { family: string; weight: number; src: string }) => ({
                  fontFamily: font.family,
                  fontWeight: font.weight,
                  src: font.src,
                  fontStyle: 'normal',
                  fontDisplay: 'swap',
                }),
              ),
              // The chat is a fixed-height shell whose message list scrolls on its
              // own; stop a swipe that reaches a scroll edge from chaining to the
              // document, which on mobile bounces the page and fires pull-to-refresh.
              'html, body': { overscrollBehavior: 'none' },
              body: { fontWeight: clientConfig.theme.typography.weights?.body ?? 400 },
              'body .chakra-heading': { fontWeight: clientConfig.theme.typography.weights?.heading },
            },
          },
          colors: {
            brand: makePalette(
              clientConfig.theme.colors.primary,
              {
                50: 0.94,
                100: 0.86,
                200: 0.72,
                300: 0.5,
                400: 0.3,
                500: 0,
                600: -0.1,
                700: -0.2,
                800: -0.3,
                900: -0.4,
              },
              tintShades,
            ),
            // Legacy aliases: many components still reference `primary` and
            // `colorScheme='primaryScheme'` from the removed clientTheme.ts.
            // Without these tokens Chakra resolves them to nothing and solid
            // buttons render invisible.
            primary: clientConfig.theme.colors.primary,
            primaryScheme: makePalette(
              clientConfig.theme.colors.primary,
              {
                50: 0.94,
                100: 0.86,
                200: 0.72,
                300: 0.5,
                400: 0.3,
                500: 0,
                600: -0.08,
                700: -0.16,
                800: -0.28,
                900: -0.4,
              },
              tintShades,
            ),
            secondary: {
              500: clientConfig.theme.colors.secondary,
            },
            accent: {
              500: clientConfig.theme.colors.accent,
            },
          },
          // `app.*` tokens are referenced by IconSelector, AgentForm,
          // ErrorState and LoadingState; without definitions Chakra emits the
          // literal token name as CSS (e.g. `background: app.bg`), which the
          // browser drops — surfaces as transparent popup/overlay backgrounds.
          semanticTokens: {
            colors: {
              // Hover / open-menu surfaces for neutral controls. Light mode is a
              // pale grey and a branded tint; dark mode steps only slightly up
              // from the gray.800 page so white text stays readable instead of
              // flashing a near-white block.
              'chat.canvas': { default: chatLight.canvas, _dark: chatDark.canvas },
              'chat.sidebar': { default: chatLight.sidebar, _dark: chatDark.sidebar },
              'chat.composer': { default: chatLight.composer, _dark: chatDark.composer },
              'chat.userBubble': {
                default: chatLight.userBubble ?? mix(chatPrimary, '#ffffff', 0.9),
                _dark: chatDark.userBubble ?? mix(chatBrandDark, chatDark.canvas, 0.8),
              },
              'chat.userInk': {
                default: chatLight.userInk ?? mix(chatPrimary, '#000000', 0.45),
                _dark: chatDark.userInk ?? mix(chatPrimary, '#ffffff', 0.88),
              },
              'chat.bubble': { default: chatLight.bubble, _dark: chatDark.bubble },
              'chat.selected': {
                default: chatLight.selected ?? mix(chatPrimary, '#ffffff', 0.94),
                _dark: chatDark.selected ?? mix(chatBrandDark, chatDark.canvas, 0.85),
              },
              'chat.brand': { default: chatPrimary, _dark: chatBrandDark },
              'chat.action': { default: chatPrimary, _dark: chatPrimary },
              'chat.actionHover': { default: darken(chatPrimary, 0.05), _dark: darken(chatPrimary, 0.05) },
              'chat.iconSurface': { default: chatIconSurface, _dark: mix(chatBrandDark, chatDark.canvas, 0.8) },
              'chat.iconInk': { default: chatPrimary, _dark: chatBrandDark },
              'chat.focus': { default: chatPrimary, _dark: chatBrandDark },
              'chat.border': { default: chatLight.border, _dark: chatDark.border },
              'chat.ink': { default: chatLight.ink, _dark: chatDark.ink },
              'chat.muted': { default: chatLight.muted, _dark: chatDark.muted },
              'chat.paper': { default: chatLight.paper, _dark: chatDark.paper },
              buttonHover: { default: '#EDF2F7', _dark: 'gray.700' },
              buttonActive: {
                default: tint ?? mix(clientConfig.theme.colors.primary, '#ffffff', 0.85),
                _dark: 'gray.600',
              },
              'app.bg': { default: 'white', _dark: 'gray.800' },
              'app.bg.page': { default: 'gray.50', _dark: 'gray.900' },
              'app.bg.subtle': { default: 'gray.100', _dark: 'gray.700' },
              'app.bg.hover': { default: 'gray.50', _dark: 'gray.700' },
              'app.border': { default: 'gray.200', _dark: 'gray.600' },
              'app.text.primary': { default: 'gray.800', _dark: 'white' },
              'app.text.secondary': { default: 'gray.600', _dark: 'gray.300' },
              'app.text.muted': { default: 'gray.500', _dark: 'gray.400' },
            },
          },
          fonts: {
            heading: clientConfig.theme.typography.headingFontFamily,
            body: clientConfig.theme.typography.fontFamily,
          },
          fontWeights: clientConfig.theme.typography.weights
            ? {
                normal: clientConfig.theme.typography.weights.body,
                medium: clientConfig.theme.typography.weights.body,
                semibold: clientConfig.theme.typography.weights.body,
                bold: clientConfig.theme.typography.weights.heading,
              }
            : undefined,
          shadows: {
            'chat.input': '0 2px 12px rgba(0, 0, 0, 0.06), 0 0 1px rgba(0, 0, 0, 0.08)',
          },
          radii: {
            base: clientConfig.theme.layout.borderRadius,
            md: clientConfig.theme.layout.borderRadius,
            // Chat steps that fall between Chakra's defaults.
            'chat.sm': '0.25rem', // 4px
            'chat.md': '0.375rem', // 6px
            'chat.bubble': '0.875rem', // 14px
          },
          fontSizes: {
            'chat.body': '0.9375rem', // 15px
            'chat.title': '1.625rem', // 26px
          },
          sizes: {
            'chat.search': '3.25rem', // 52px
          },
          // Add llmTools configuration for KnowledgeSourceBadge
          llmTools: {
            none: {
              backgroundColor: '#FAD9E2',
              color: '#6D1D31',
            },
            sharepointSearch: {
              backgroundColor: '#C2EBF1',
              color: '#174A50',
            },
            internet: {
              backgroundColor: '#E2E8F0',
              textColor: '#1A202C',
            },
          },
          components: {
            Button: {
              defaultProps: {
                colorScheme: 'brand',
              },
              variants: {
                ...touchSafeButtonVariants,
                // Composer toolbar controls (the + and model-picker buttons): muted
                // resting color, a subtle grey on hover, a branded tint while their
                // menu is open (`_active` also matches `[data-active]`).
                inputControl: touchSafeHover(props => ({
                  color: props.colorMode === 'dark' ? 'gray.400' : 'gray.500',
                  _hover: { bg: 'buttonHover' },
                  _active: { bg: 'buttonActive', color: props.colorMode === 'dark' ? 'white' : 'gray.800' },
                })),
              },
            },
            Menu: {
              baseStyle: {
                item: touchSafeMenuItem,
              },
            },
          },
        })
        setTheme(chakraTheme)

        // Update favicon and title
        updateFavicon(clientConfig.theme.branding.faviconUrl)
        document.title = `${
          clientConfig.theme.branding.assistantName || clientConfig.theme.branding.companyName
        } - AI Assistant`

        console.log(`Loaded build-time config for client: ${clientConfig.client_id}`, clientConfig)
      } catch (error) {
        console.error('Error loading build-time client config:', error)
        // Set default config on error
        setConfig(getDefaultConfig(clientId))
        setTheme(extendTheme({})) // Default Chakra theme
      } finally {
        setIsLoading(false)
      }
    }

    loadBuildTimeConfig()
  }, [clientId])

  console.log('config', config)

  const value: ClientContextType = {
    config,
    isLoading,
    theme,
  }

  if (isLoading || !theme) {
    return <div>Loading client configuration...</div>
  }

  return (
    <ClientContext.Provider value={value}>
      <ChakraProvider theme={theme}>{children}</ChakraProvider>
    </ClientContext.Provider>
  )
}

// Helper functions
function updateFavicon(faviconUrl: string) {
  const link = (document.querySelector("link[rel*='icon']") as HTMLLinkElement) || document.createElement('link')
  link.type = 'image/x-icon'
  link.rel = 'shortcut icon'
  link.href = faviconUrl
  document.getElementsByTagName('head')[0].appendChild(link)
}

function getDefaultConfig(clientId: string): ClientConfig {
  return {
    client_id: clientId,
    theme: {
      name: 'Default',
      colors: {
        primary: '#3182CE',
        secondary: '#38B2AC',
        accent: '#ED8936',
        background: '#FFFFFF',
        surface: '#F7FAFC',
        text: {
          primary: '#2D3748',
          secondary: '#4A5568',
          muted: '#718096',
        },
        success: '#38A169',
        warning: '#D69E2E',
        error: '#E53E3E',
      },
      branding: {
        logoUrl: '/default-logo.png',
        faviconUrl: '/favicon.ico',
        companyName: 'AI Assistant',
        tagline: 'Powered by AI',
        supportEmail: 'support@example.com',
        backgroundImage: '/assets/static/images/background.svg',
      },
      typography: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        headingFontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        weights: { body: 500, heading: 700 },
      },
      layout: {
        sidebarWidth: '280px',
        headerHeight: '64px',
        borderRadius: '8px',
      },
      customTexts: {
        welcomeMessage: 'Welcome to AI Assistant',
        chatPlaceholder: 'Ask me anything...',
        footerText: 'Powered by AI Technology',
      },
    },
  }
}
